import * as React from 'react'
import clx from 'classnames'

import { CardConfig, importsHomeCardsConfig } from './config'
import { ImportsHomeCard } from './components/ImportsHomeCard'
import { ImportPopupContainer } from '@/modules/imports/container/import-popup-container'
import { WebcardImportPopupContainer } from '@/modules/imports/container/webcard-import-popup-container'
import { SimpleImportPopupContainer } from '@/modules/imports/container/simple-import-popup-container'
import { ChartImportPopupContainer } from '@/modules/imports/container/scan-discs-popup-container'
import { WebfleetBordersImportPopupContainer } from '@/modules/imports/container/webfleet-borders-import-popup-container'
import { useImportsUploadingType } from '@/modules/imports/stores/importsUploadingTypeStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Popup } from '@/ui/overlays/Popup'
import { ImportSource } from '../../enums'

type Props = {
  companyId: string
  onSuccess?: () => void
  onChartSuccess?: () => void
  cards?: CardConfig[]
  className?: string
  gridClassName?: string
  modal?: {
    open: boolean
    onClose: () => void
    title?: string
    description?: string
  }
}

const DEFAULT_GRID_CLASSNAME =
  'tw-grid tw-grid-cols-1 tw-gap-px tw-overflow-hidden tw-border-t tw-border-slate-200 tw-bg-slate-200 sm:tw-grid-cols-2 lg:tw-grid-cols-4'

export const ImportsCardsPanel = ({
  companyId,
  onSuccess,
  onChartSuccess,
  cards = importsHomeCardsConfig.cards,
  className,
  gridClassName,
  modal,
}: Props) => {
  const { t } = useLocalize()
  const { uploadingType, openUploadingType, closeUploadingType } =
    useImportsUploadingType()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { name } = e.currentTarget
    openUploadingType(name as ImportSource)
    modal?.onClose()
  }

  React.useEffect(() => closeUploadingType, [closeUploadingType])

  const grid = (
    <div className="tw-rounded-lg tw-bg-white tw-shadow-sm">
      <div className={clx(DEFAULT_GRID_CLASSNAME, gridClassName)}>
        {cards.map((c) => (
          <ImportsHomeCard
            key={c.name}
            title={t(c.titleKey)}
            icon={c.icon}
            faIcon={c.faIcon}
            description={t(c.descriptionKey)}
            handleClick={handleClick}
            name={c.name}
          />
        ))}
      </div>
    </div>
  )

  return (
    <div className={clx('tw-h-fit', className)}>
      <ImportPopupContainer
        isOpen={
          Boolean(uploadingType) &&
          uploadingType !== ImportSource.DriverCard &&
          uploadingType !== ImportSource.Gps &&
          uploadingType !== ImportSource.ScanDiscs &&
          uploadingType !== ImportSource.TachographDiscs &&
          uploadingType !== ImportSource.WebfleetBorders
        }
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onSuccess}
      />
      <WebcardImportPopupContainer
        isOpen={uploadingType === ImportSource.DriverCard}
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onSuccess}
      />
      <SimpleImportPopupContainer
        isOpen={uploadingType === ImportSource.Gps}
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onSuccess}
      />
      <WebfleetBordersImportPopupContainer
        isOpen={uploadingType === ImportSource.WebfleetBorders}
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onSuccess}
      />
      {/* "Wczytaj tarczki" = WPF ReadDataFromTachoChartCommand (pliki ze skanami),
          "Skanuj tarczki" = WPF ReadDataFromScannerCommand (skaner przez mostek). */}
      <ChartImportPopupContainer
        isOpen={uploadingType === ImportSource.TachographDiscs}
        source="file"
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onChartSuccess ?? onSuccess}
      />
      <ChartImportPopupContainer
        isOpen={uploadingType === ImportSource.ScanDiscs}
        source="scanner"
        handleClose={closeUploadingType}
        companyId={companyId}
        onSuccess={onChartSuccess ?? onSuccess}
      />

      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="fa-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2C57FF" />
            <stop offset="100%" stopColor="#48A1FF" />
          </linearGradient>
        </defs>
      </svg>

      {modal ? (
        <Popup
          open={modal.open}
          onClose={modal.onClose}
          contentClassName="tw-w-[95vw] tw-max-w-[1200px] !tw-p-0"
        >
          {(modal.title || modal.description) && (
            <div className="tw-border-b tw-border-base-300 tw-px-6 tw-py-4 tw-pr-12">
              {modal.title && (
                <h2 className="tw-mb-0 tw-text-lg tw-font-semibold">
                  {modal.title}
                </h2>
              )}
              {modal.description && (
                <p className="tw-mt-1 tw-text-sm tw-text-content-base/70">
                  {modal.description}
                </p>
              )}
            </div>
          )}
          {grid}
        </Popup>
      ) : (
        grid
      )}
    </div>
  )
}
